import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { centralToday } from '../utils/centralDate';
import './SalesDashboard.css';

type Unit = '$' | 'ea' | 'bbl' | 'CE';
type GroupBy = 'product' | 'item' | 'customer';
type TrendGrain = 'week' | 'month' | 'quarter';

type KpiBlock = {
  label: string;
  start: string;
  end: string;
  value: number;
  priorValue: number;
  pctChange: number | null;
  priorLabel: string;
};

type SeriesPoint = { key: string; label: string; value: number };

type DashboardPayload = {
  asOf: string;
  refreshedAt?: string;
  unit: Unit;
  filters: Record<string, unknown>;
  meta: {
    salesRepGrouping: boolean;
    salesRepNote?: string;
    ceMethod?: string;
    projectionMethod?: string;
    revenueNote?: string;
    units: Unit[];
    packagingStyles: string[];
    groupByOptions: GroupBy[];
    trendGrains: TrendGrain[];
  };
  sites: { siteId: string; name: string }[];
  products: string[];
  kpis: { year: KpiBlock; month: KpiBlock; week: KpiBlock };
  trends: {
    grain: TrendGrain;
    groupBy: GroupBy;
    buckets: { key: string; label: string }[];
    series: { name: string; points: SeriesPoint[]; total: number }[];
  };
  yearOverYear: {
    year: number;
    priorYear: number;
    currentMonth: number;
    projectionMethod: string;
    months: { month: number; label: string }[];
    consolidated: {
      prior: number[];
      current: number[];
      projected: (number | null)[];
    };
    series: {
      name: string;
      prior: number[];
      current: number[];
      projected: (number | null)[];
    }[];
  };
  monthlyTable: {
    columns: {
      priorMonth: { label: string };
      currentMonth: { label: string };
      currentProjection: { label: string };
      nextProjection: { label: string };
    };
    rows: {
      name: string;
      priorMonth: number;
      currentMonth: number;
      currentProjection: number;
      nextProjection: number;
    }[];
  };
  distributors: {
    buckets: { key: string; label: string }[];
    series: { name: string; points: SeriesPoint[] }[];
  };
  commission: {
    label: string;
    note: string;
    monthLabel: string;
    distributors: {
      name: string;
      ceToDate: number;
      baseline: number;
      ceOverBaseline: number;
      estimatedCommission: number;
    }[];
  };
};

type DrillInvoice = {
  invoiceId: number;
  invoiceNumber: string;
  date: string;
  customer: string;
  product: string;
  package: string;
  itemName: string;
  quantity: number;
  amount: number;
};

const GROUP_LABELS: Record<GroupBy, string> = {
  product: 'By Product',
  item: 'By Item',
  customer: 'By Customer',
};

const SERIES_COLORS = [
  '#1a3a5c',
  '#3d7ea6',
  '#5ba3c9',
  '#2d6a4f',
  '#b08968',
  '#9b2226',
  '#6c757d',
  '#495057',
  '#0d9488',
  '#7c3aed',
  '#c2410c',
  '#0369a1',
];

function formatValue(n: number, unit: Unit): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (unit === '$') {
    if (abs >= 1000) {
      const k = n / 1000;
      return `$${k.toFixed(Math.abs(k) >= 100 ? 0 : 2)}K`;
    }
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (abs >= 1000) return `${(n / 1000).toFixed(2)}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatExact(n: number, unit: Unit): string {
  if (unit === '$') {
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

function PctChange({ value, label }: { value: number | null; label: string }) {
  if (value == null) {
    return <span className="sd-kpi-change sd-kpi-change--flat">n/a {label}</span>;
  }
  const up = value > 0;
  const flat = value === 0;
  const cls = flat ? 'flat' : up ? 'up' : 'down';
  const arrow = flat ? '→' : up ? '↑' : '↓';
  const sign = value > 0 ? '+' : '';
  return (
    <span className={`sd-kpi-change sd-kpi-change--${cls}`}>
      {arrow} {sign}
      {value}% {label}
    </span>
  );
}

type BarChartProps = {
  categories: string[];
  series: { name: string; values: number[]; color: string; hidden?: boolean }[];
  unit: Unit;
  height?: number;
  onBarClick?: (categoryIndex: number, seriesName: string) => void;
  clustered?: boolean;
};

const SimpleBarChart: React.FC<BarChartProps> = ({
  categories,
  series,
  unit,
  height = 220,
  onBarClick,
  clustered = false,
}) => {
  const visible = series.filter((s) => !s.hidden);
  const max = Math.max(
    1,
    ...visible.flatMap((s) => s.values.map((v) => (v == null || Number.isNaN(v) ? 0 : Math.abs(v))))
  );
  const pad = { top: 12, right: 8, bottom: 36, left: 48 };
  const width = Math.max(320, categories.length * (clustered ? 36 : 28));
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const groupW = innerW / Math.max(categories.length, 1);
  const barCount = Math.max(visible.length, 1);
  const barW = clustered ? Math.min(10, (groupW * 0.7) / barCount) : Math.min(22, groupW * 0.55);

  return (
    <div className="sd-chart-scroll">
      <svg
        className="sd-chart"
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        role="img"
        aria-label="Bar chart"
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = pad.top + innerH * (1 - t);
          const val = max * t;
          return (
            <g key={t}>
              <line
                x1={pad.left}
                x2={width - pad.right}
                y1={y}
                y2={y}
                stroke="#e8ecf0"
                strokeWidth={1}
              />
              <text x={pad.left - 6} y={y + 3} textAnchor="end" className="sd-chart-axis">
                {formatValue(val, unit)}
              </text>
            </g>
          );
        })}
        {categories.map((cat, i) => {
          const cx = pad.left + groupW * i + groupW / 2;
          return (
            <g key={cat + i}>
              {visible.map((s, si) => {
                const v = s.values[i] || 0;
                if (v == null || Number.isNaN(v)) return null;
                const h = (Math.abs(v) / max) * innerH;
                const x = clustered
                  ? cx - (barCount * barW) / 2 + si * barW
                  : cx - barW / 2;
                const y = pad.top + innerH - h;
                return (
                  <rect
                    key={s.name}
                    x={x}
                    y={y}
                    width={barW}
                    height={Math.max(h, v ? 1 : 0)}
                    fill={s.color}
                    className={onBarClick ? 'sd-bar-clickable' : undefined}
                    onClick={() => onBarClick?.(i, s.name)}
                  >
                    <title>{`${s.name} · ${cat}: ${formatExact(v, unit)}`}</title>
                  </rect>
                );
              })}
              <text
                x={cx}
                y={height - 10}
                textAnchor="middle"
                className="sd-chart-axis sd-chart-cat"
              >
                {cat.length > 8 ? cat.slice(0, 7) + '…' : cat}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const SalesDashboard: React.FC = () => {
  const [siteId, setSiteId] = useState('All');
  const [packagingStyle, setPackagingStyle] = useState('All');
  const [unit, setUnit] = useState<Unit>('$');
  const [includeTaproomVolume, setIncludeTaproomVolume] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupBy>('product');
  const [trendGrain, setTrendGrain] = useState<TrendGrain>('month');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [productMenuOpen, setProductMenuOpen] = useState(false);
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<{
    title: string;
    invoices: DrillInvoice[];
    loading: boolean;
  } | null>(null);

  const queryString = useMemo(() => {
    const q = new URLSearchParams();
    q.set('asOf', centralToday());
    q.set('siteId', siteId);
    q.set('packagingStyle', packagingStyle);
    q.set('unit', unit);
    q.set('groupBy', groupBy);
    q.set('trendGrain', trendGrain);
    if (includeTaproomVolume) q.set('includeTaproomVolume', '1');
    if (selectedProducts.length) q.set('products', selectedProducts.join('|'));
    return q.toString();
  }, [siteId, packagingStyle, unit, groupBy, trendGrain, includeTaproomVolume, selectedProducts]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/reports/sales-dashboard?${queryString}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || `HTTP ${res.status}`);
      }
      const json = (await res.json()) as DashboardPayload;
      setData(json);
      setHiddenSeries({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    load();
  }, [load]);

  const openDrilldown = async (opts: {
    title: string;
    startDate: string;
    endDate: string;
    groupKey?: string;
  }) => {
    setDrill({ title: opts.title, invoices: [], loading: true });
    try {
      const q = new URLSearchParams(queryString);
      q.set('drilldown', '1');
      q.set('startDate', opts.startDate);
      q.set('endDate', opts.endDate);
      if (opts.groupKey) q.set('groupKey', opts.groupKey);
      const res = await apiFetch(`/api/reports/sales-dashboard?${q.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setDrill({
        title: opts.title,
        invoices: json.invoices || [],
        loading: false,
      });
    } catch (err) {
      setDrill({
        title: opts.title,
        invoices: [],
        loading: false,
      });
      setError(err instanceof Error ? err.message : 'Drill-down failed');
    }
  };

  const filteredProductOptions = useMemo(() => {
    const list = data?.products || [];
    const q = productSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => p.toLowerCase().includes(q));
  }, [data?.products, productSearch]);

  const trendSeries = useMemo(() => {
    if (!data) return [];
    return data.trends.series.map((s, i) => ({
      name: s.name,
      color: SERIES_COLORS[i % SERIES_COLORS.length],
      values: s.points.map((p) => p.value),
      hidden: !!hiddenSeries[s.name],
    }));
  }, [data, hiddenSeries]);

  const yoySeries = useMemo(() => {
    if (!data) return [];
    const c = data.yearOverYear.consolidated;
    return [
      {
        name: String(data.yearOverYear.priorYear),
        color: '#8eb8d4',
        values: c.prior,
      },
      {
        name: String(data.yearOverYear.year),
        color: '#1a3a5c',
        values: c.current,
      },
      {
        name: 'Projected',
        color: '#6b8f71',
        values: c.projected.map((v) => (v == null ? 0 : v)),
      },
    ];
  }, [data]);

  const distSeries = useMemo(() => {
    if (!data) return [];
    return data.distributors.series.map((s, i) => ({
      name: s.name,
      color: i === 0 ? '#1a3a5c' : '#c45c26',
      values: s.points.map((p) => p.value),
    }));
  }, [data]);

  return (
    <div className="page-container sales-dashboard sd-root" data-testid="sales-dashboard">
      <header className="sd-header">
        <div className="sd-title-block">
          <h2 className="app-header sd-title">Sales Dashboard</h2>
          <span className="sd-refresh-note">DATA REFRESHED DAILY</span>
        </div>
        <div className="sd-global-controls">
          <label className="sd-field">
            <span>Site</span>
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)} aria-label="Site">
              {(data?.sites || [{ siteId: 'All', name: 'All' }]).map((s) => (
                <option key={s.siteId} value={s.siteId}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="sd-field">
            <span>Packaging Style</span>
            <select
              value={packagingStyle}
              onChange={(e) => setPackagingStyle(e.target.value)}
              aria-label="Packaging Style"
            >
              {(data?.meta.packagingStyles || ['All', 'Single', 'Case', 'Keg', 'Bulk']).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <div className="sd-unit-toggle" role="group" aria-label="Unit">
            {(data?.meta.units || (['$', 'ea', 'bbl', 'CE'] as Unit[])).map((u) => (
              <button
                key={u}
                type="button"
                className={unit === u ? 'active' : ''}
                onClick={() => setUnit(u)}
              >
                {u}
              </button>
            ))}
          </div>
          {unit !== '$' && (
            <label className="sd-toggle">
              <input
                type="checkbox"
                checked={includeTaproomVolume}
                onChange={(e) => setIncludeTaproomVolume(e.target.checked)}
              />
              Include taproom volume
            </label>
          )}
        </div>
      </header>

      {error && <div className="error">{error}</div>}
      {loading && !data && <p className="sd-loading">Loading sales…</p>}

      {data && (
        <>
          <section className="sd-kpis" aria-label="KPI cards">
            {(['year', 'month', 'week'] as const).map((key) => {
              const k = data.kpis[key];
              return (
                <button
                  type="button"
                  key={key}
                  className="sd-kpi"
                  onClick={() =>
                    openDrilldown({
                      title: `${k.label} · ${formatExact(k.value, unit)}`,
                      startDate: k.start,
                      endDate: k.end,
                    })
                  }
                >
                  <div className="sd-kpi-label">{k.label}</div>
                  <div className="sd-kpi-value">{formatValue(k.value, unit)}</div>
                  <PctChange value={k.pctChange} label={k.priorLabel} />
                </button>
              );
            })}
          </section>

          <section className="sd-commission" aria-label="Commission tracker">
            <div className="sd-card-head">
              <h3>Commission tracker</h3>
              <span className="sd-badge">{data.commission.label}</span>
            </div>
            <p className="sd-note">{data.commission.note}</p>
            <p className="sd-muted">{data.commission.monthLabel} CE to date</p>
            <div className="sd-commission-grid">
              {data.commission.distributors.map((d) => (
                <div key={d.name} className="sd-commission-item">
                  <div className="sd-commission-name">{d.name}</div>
                  <div>
                    {d.ceToDate.toLocaleString(undefined, { maximumFractionDigits: 1 })} CE
                    <span className="sd-muted"> / baseline {d.baseline}</span>
                  </div>
                  <div>
                    Over baseline:{' '}
                    {d.ceOverBaseline.toLocaleString(undefined, { maximumFractionDigits: 1 })} CE
                  </div>
                  <div className="sd-commission-pay">
                    Est. commission: ${d.estimatedCommission.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="sd-grid">
            <section className="sd-panel sd-trends">
              <div className="sd-card-head">
                <h3>Sales Trends</h3>
                <div className="sd-inline-controls">
                  <select
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                    aria-label="Group by"
                  >
                    {(data.meta.groupByOptions || (['product', 'item', 'customer'] as GroupBy[])).map(
                      (g) => (
                        <option key={g} value={g}>
                          {GROUP_LABELS[g]}
                        </option>
                      )
                    )}
                  </select>
                  <div className="sd-grain" role="group" aria-label="Trend grain">
                    {(['week', 'month', 'quarter'] as TrendGrain[]).map((g) => (
                      <button
                        key={g}
                        type="button"
                        className={trendGrain === g ? 'active' : ''}
                        onClick={() => setTrendGrain(g)}
                      >
                        {g[0].toUpperCase() + g.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div className="sd-product-filter">
                    <button
                      type="button"
                      className="sd-product-btn"
                      onClick={() => setProductMenuOpen((o) => !o)}
                    >
                      Product: {selectedProducts.length ? `${selectedProducts.length} selected` : 'All'}
                    </button>
                    {productMenuOpen && (
                      <div className="sd-product-menu">
                        <input
                          type="search"
                          placeholder="Search products"
                          value={productSearch}
                          onChange={(e) => setProductSearch(e.target.value)}
                          aria-label="Search products"
                        />
                        <button
                          type="button"
                          className="sd-linkish"
                          onClick={() => setSelectedProducts([])}
                        >
                          Clear
                        </button>
                        <ul>
                          {filteredProductOptions.map((p) => {
                            const checked = selectedProducts.includes(p);
                            return (
                              <li key={p}>
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() =>
                                      setSelectedProducts((prev) =>
                                        checked ? prev.filter((x) => x !== p) : [...prev, p]
                                      )
                                    }
                                  />
                                  {p}
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <SimpleBarChart
                categories={data.trends.buckets.map((b) => b.label)}
                series={trendSeries}
                unit={unit}
                onBarClick={(idx, seriesName) => {
                  const bucket = data.trends.buckets[idx];
                  if (!bucket) return;
                  let start = bucket.key;
                  let end = bucket.key;
                  if (data.trends.grain === 'month') {
                    start = `${bucket.key}-01`;
                    const [y, m] = bucket.key.split('-').map(Number);
                    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
                    end = `${bucket.key}-${String(last).padStart(2, '0')}`;
                  } else if (data.trends.grain === 'week') {
                    end = (() => {
                      const [y, m, d] = bucket.key.split('-').map(Number);
                      const dt = new Date(Date.UTC(y, m - 1, d));
                      dt.setUTCDate(dt.getUTCDate() + 6);
                      return dt.toISOString().slice(0, 10);
                    })();
                  } else {
                    const [ys, qs] = bucket.key.split('-Q');
                    const y = Number(ys);
                    const q = Number(qs);
                    const sm = (q - 1) * 3 + 1;
                    start = `${y}-${String(sm).padStart(2, '0')}-01`;
                    const em = sm + 2;
                    const last = new Date(Date.UTC(y, em, 0)).getUTCDate();
                    end = `${y}-${String(em).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
                  }
                  openDrilldown({
                    title: `${seriesName} · ${bucket.label}`,
                    startDate: start,
                    endDate: end > data.asOf ? data.asOf : end,
                    groupKey: seriesName === 'Other' ? undefined : seriesName,
                  });
                }}
              />
              <div className="sd-legend">
                {trendSeries.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    className={`sd-legend-item ${s.hidden ? 'is-hidden' : ''}`}
                    onClick={() =>
                      setHiddenSeries((prev) => ({ ...prev, [s.name]: !prev[s.name] }))
                    }
                  >
                    <span className="sd-swatch" style={{ background: s.color }} />
                    {s.name}
                  </button>
                ))}
              </div>
            </section>

            <section className="sd-panel sd-monthly">
              <div className="sd-card-head">
                <h3>Monthly Sales</h3>
              </div>
              <div className="sd-table-wrap">
                <table className="sd-table">
                  <thead>
                    <tr>
                      <th>{GROUP_LABELS[groupBy].replace(/^By /, '')}</th>
                      <th>{data.monthlyTable.columns.priorMonth.label}</th>
                      <th>{data.monthlyTable.columns.currentMonth.label}</th>
                      <th>{data.monthlyTable.columns.currentProjection.label}</th>
                      <th>{data.monthlyTable.columns.nextProjection.label}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.monthlyTable.rows.map((r) => (
                      <tr key={r.name}>
                        <td>{r.name}</td>
                        <td>
                          <button
                            type="button"
                            className="sd-cell-btn"
                            onClick={() => {
                              const label = data.monthlyTable.columns.priorMonth.label;
                              const ym = data.asOf.slice(0, 7);
                              const [y, m] = ym.split('-').map(Number);
                              const py = y - 1;
                              const start = `${py}-${String(m).padStart(2, '0')}-01`;
                              const last = new Date(Date.UTC(py, m, 0)).getUTCDate();
                              openDrilldown({
                                title: `${r.name} · ${label}`,
                                startDate: start,
                                endDate: `${py}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`,
                                groupKey: r.name,
                              });
                            }}
                          >
                            {formatExact(r.priorMonth, unit)}
                          </button>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="sd-cell-btn"
                            onClick={() => {
                              const ym = data.asOf.slice(0, 7);
                              openDrilldown({
                                title: `${r.name} · ${data.monthlyTable.columns.currentMonth.label}`,
                                startDate: `${ym}-01`,
                                endDate: data.asOf,
                                groupKey: r.name,
                              });
                            }}
                          >
                            {formatExact(r.currentMonth, unit)}
                          </button>
                        </td>
                        <td>{formatExact(r.currentProjection, unit)}</td>
                        <td>{formatExact(r.nextProjection, unit)}</td>
                      </tr>
                    ))}
                    {!data.monthlyTable.rows.length && (
                      <tr>
                        <td colSpan={5} className="sd-muted">
                          No sales in this period
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <section className="sd-panel sd-yoy">
            <div className="sd-card-head">
              <h3>Year over Year Sales</h3>
              <span className="sd-note">{data.yearOverYear.projectionMethod}</span>
            </div>
            <SimpleBarChart
              categories={data.yearOverYear.months.map((m) => m.label)}
              series={yoySeries}
              unit={unit}
              clustered
              height={240}
              onBarClick={(idx) => {
                const m = data.yearOverYear.months[idx];
                if (!m) return;
                const y = data.yearOverYear.year;
                const start = `${y}-${String(m.month).padStart(2, '0')}-01`;
                const last = new Date(Date.UTC(y, m.month, 0)).getUTCDate();
                let end = `${y}-${String(m.month).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
                if (end > data.asOf) end = data.asOf;
                openDrilldown({
                  title: `${m.label} ${y}`,
                  startDate: start,
                  endDate: end,
                });
              }}
            />
            <div className="sd-legend">
              {yoySeries.map((s) => (
                <span key={s.name} className="sd-legend-item">
                  <span className="sd-swatch" style={{ background: s.color }} />
                  {s.name}
                </span>
              ))}
            </div>
          </section>

          <section className="sd-panel sd-dist">
            <div className="sd-card-head">
              <h3>Distributor comparison</h3>
              <span className="sd-muted">Gulf Distributing vs United Johnson Brothers</span>
            </div>
            <SimpleBarChart
              categories={data.distributors.buckets.map((b) => b.label)}
              series={distSeries}
              unit={unit}
              clustered
              height={220}
            />
            <div className="sd-legend">
              {distSeries.map((s) => (
                <span key={s.name} className="sd-legend-item">
                  <span className="sd-swatch" style={{ background: s.color }} />
                  {s.name}
                </span>
              ))}
            </div>
          </section>

          {data.meta.salesRepNote && (
            <p className="sd-footnote">{data.meta.salesRepNote}</p>
          )}
        </>
      )}

      {drill && (
        <div className="sd-modal-backdrop" role="presentation" onClick={() => setDrill(null)}>
          <div
            className="sd-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Invoice drill-down"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sd-modal-head">
              <h3>{drill.title}</h3>
              <button type="button" onClick={() => setDrill(null)} aria-label="Close">
                ×
              </button>
            </div>
            {drill.loading ? (
              <p className="sd-loading">Loading invoices…</p>
            ) : (
              <div className="sd-table-wrap">
                <table className="sd-table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Product</th>
                      <th>Package</th>
                      <th>Qty</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drill.invoices.map((inv, i) => (
                      <tr key={`${inv.invoiceId}-${i}`}>
                        <td>
                          <Link to={`/invoices/${inv.invoiceId}`}>{inv.invoiceNumber}</Link>
                        </td>
                        <td>{inv.date}</td>
                        <td>{inv.customer}</td>
                        <td>{inv.product}</td>
                        <td>{inv.package}</td>
                        <td>{inv.quantity}</td>
                        <td>{formatExact(inv.amount, '$')}</td>
                      </tr>
                    ))}
                    {!drill.invoices.length && (
                      <tr>
                        <td colSpan={7} className="sd-muted">
                          No invoices in this selection
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesDashboard;
